import { getDirectusInstance, resolveTranslation } from "@fhlib/directus";
import { HomeSheetTemplate } from "@templates/home-sheet/home-sheet";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useCallback, useMemo } from "react";
import * as dateFns from "date-fns";
import { SearchForm } from "@organisms/searcher";
import {
  BookingHandler,
  CalculatePriceHandler,
} from "@organisms/dialog-booking";
import { GetCalendarHandler } from "@molecules/booking-form-fields/interfaces";
import * as showdown from "showdown";
import Head from "next/head";
import { useTranslation } from "next-i18next";
import { ContactFormData } from "@molecules/contact-form/types";
import { useFHConfig } from "@templates/config";

export const getServerSideProps: GetServerSideProps<HomeSheetProps> = async ({
  locale,
  defaultLocale,
  query,
  params,
}) => {
  const directus = await getDirectusInstance();

  if (!params) {
    throw new Error("Params invalid");
  }

  let property_id =
    "slug" in params ? parseInt(params.slug as string) : undefined;

  if (property_id === undefined) {
    throw new Error("Invalid slug");
  }

  if (isNaN(property_id)) {
    const propertyTranslations = await directus
      .items("properties_translations")
      .readByQuery({
        fields: ["properties_id"],
        limit: 1,
        filter: {
          slug: params.slug as string,
        },
      });

    if (propertyTranslations.data && propertyTranslations.data.length > 0) {
      property_id = propertyTranslations.data[0].properties_id as any as number;
    }
  }

  if (property_id === undefined) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
      props: {},
    };
  }

  const check_in = "check_in" in query ? (query.check_in as string) : undefined;
  const check_out =
    "check_out" in query ? (query.check_out as string) : undefined;
  const guests =
    "guests" in query ? parseInt(query.guests as string, 10) : undefined;

  const propertiesResponse = await directus.transport.post(
    "/booking/availability",
    { property_id, check_in, guests, check_out }
  );

  const {
    bedrooms,
    accommodates_max,
    price,
    min_price,
    translations,
    amenities,
    images,
    featured_image,
  } = propertiesResponse.raw;

  const defaultTranslated = resolveTranslation("en", "en", translations) as any;

  const translated = resolveTranslation(
    locale,
    defaultLocale,
    translations
  ) as any;

  const title =
    !translated.title || translated.title.length === 0
      ? defaultTranslated.title
      : translated.title;

  let description =
    !translated.description || translated.description.length === 0
      ? defaultTranslated.description
      : translated.description;

  if (description) {
    const converter = new showdown.Converter();
    description = converter.makeHtml(description);
  }

  const amenitiesMapped: { code: string; name: string }[] = [];

  for (const amenity of amenities) {
    const translatedAmenity = resolveTranslation(
      locale,
      defaultLocale,
      amenity.translations
    ) as any;
    switch (amenity.id) {
      case 85:
        amenitiesMapped.push({
          code: "satellite-tv",
          name: translatedAmenity.name ?? "Satellite TV",
        });
        break;
      case 82:
        amenitiesMapped.push({
          code: "parking",
          name: translatedAmenity.name ?? "Parking",
        });
        break;
      case 175:
        amenitiesMapped.push({
          code: "basic-amenities",
          name: translatedAmenity.name ?? "Basic amenities",
        });
        break;
      case 75:
        amenitiesMapped.push({
          code: "hairdryer",
          name: translatedAmenity.name ?? "Hairdryer",
        });
        break;
      case 91:
        amenitiesMapped.push({
          code: "heating-system",
          name: translatedAmenity.name ?? "Heating System",
        });
        break;
      case 169:
        amenitiesMapped.push({
          code: "fire-extinguisher",
          name: translatedAmenity.name ?? "Fire extinguisher",
        });
        break;
      case 142:
        amenitiesMapped.push({
          code: "swimming-pool",
          name: translatedAmenity.name ?? "Swimming pool",
        });
        break;
      case 73:
        amenitiesMapped.push({
          code: "kitchen",
          name: translatedAmenity.name ?? "Kitchen",
        });
        break;
      case 74:
        amenitiesMapped.push({
          code: "dryer",
          name: translatedAmenity.name ?? "Dryer",
        });
        break;
      case 113:
        amenitiesMapped.push({
          code: "washing-machine",
          name: translatedAmenity.name ?? "Washing machine",
        });
        break;
      case 77:
        amenitiesMapped.push({ code: "tv", name: "TV" });
        break;
      case 124:
        amenitiesMapped.push({
          code: "air-conditioner",
          name: translatedAmenity.name ?? "Air Conditioner",
        });
        break;
      case 99:
      case 189:
        amenitiesMapped.push({
          code: "wifi",
          name: translatedAmenity.name ?? "Wi-Fi",
        });
        break;
    }
  }

  let photos: { resource: string; title: string }[] = [];

  if (featured_image) {
    photos.push({ title: "Featured", resource: featured_image });
  }

  const filteredImages = featured_image
    ? images.filter((x: string) => x !== featured_image)
    : images;

  for (let i = 0; i < filteredImages.length; i++) {
    photos.push({ title: "Image " + i, resource: filteredImages[i] });
  }

  const searchFormData: SearchForm = {};
  if (check_in) searchFormData.checkIn = check_in;
  if (check_out) searchFormData.checkOut = check_out;
  if (guests) searchFormData.guests = guests;

  return {
    props: {
      locale: locale ?? defaultLocale ?? "en",
      propertyId: property_id,
      doors: bedrooms,
      people: accommodates_max,
      minPrice: price ?? min_price,
      description,
      amenities: amenitiesMapped,
      title,
      photos,
      minNights: 7,
      lockedDates: [
        dateFns.format(dateFns.addDays(new Date(), 1), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 2), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 20), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 21), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 22), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 23), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 24), "yyyy-MM-dd"),
        dateFns.format(dateFns.addDays(new Date(), 25), "yyyy-MM-dd"),
      ],
      searchFormData: searchFormData,
      ...(await serverSideTranslations(locale ?? "en", [
        "common",
        "homes-sheet",
        "property",
        "forms",
        "contact",
      ])),
    },
  };
};

type HomeSheetProps = {
  locale: string;
  propertyId: number;
  title: string;
  beds?: number;
  doors?: number;
  people?: number;
  minPrice: number;
  description: string;
  amenities: { code: string; name: string }[];
  photos: { title: string; resource: string }[];
  searchFormData?: SearchForm;
  minNights: number;
  lockedDates: string[];
};

export default function HomeSheet({
  propertyId,
  title,
  beds,
  people,
  doors,
  minPrice,
  description,
  amenities,
  photos,
  searchFormData,
  locale,
}: HomeSheetProps) {
  const config = useFHConfig();

  const handleBookingRequest: BookingHandler = useCallback(
    async (data) => {
      const response = await fetch("/api/booking/checkout", {
        method: "post",
        body: JSON.stringify({
          property_id: propertyId,
          check_in: dateFns.format(data.period.checkIn as Date, "yyyy-MM-dd"),
          check_out: dateFns.format(data.period.checkOut as Date, "yyyy-MM-dd"),
          name: data.name,
          email: data.email,
          mobile: data.mobile,
          guests: data.guests,
          language: locale,
          payment_method: data.paymentMethod,
        }),
        headers: {
          "content-type": "application/json",
        },
      });

      const result = await response.json();

      if (result.checkout_url) {
        document.location.href = result.checkout_url;
        return true;
      }

      return false;
    },
    [locale, propertyId]
  );

  const handleCalculatePrice: CalculatePriceHandler = useCallback(
    async (data) => {
      const response = await fetch("/api/booking/calculate-price", {
        method: "post",
        body: JSON.stringify({
          property_id: propertyId,
          check_in: dateFns.format(data.checkIn as Date, "yyyy-MM-dd"),
          check_out: dateFns.format(data.checkOut as Date, "yyyy-MM-dd"),
        }),
        headers: {
          "content-type": "application/json",
        },
      });
      const price = await response.json();
      return price;
    },
    [propertyId]
  );

  const handleGetCalendar: GetCalendarHandler = useCallback(
    async (from: Date, to: Date) => {
      const response = await fetch("/api/booking/calendar", {
        method: "post",
        body: JSON.stringify({
          property_id: propertyId,
          from: dateFns.format(from, "yyyy-MM-dd"),
          to: dateFns.format(to, "yyyy-MM-dd"),
        }),
        headers: {
          "content-type": "application/json",
        },
      });
      const data = await response.json();
      return {
        min_stay: data.min_stay,
        locked_dates: data.locked_dates.map((c: string) =>
          dateFns.parse(c, "yyyy-MM-dd", new Date())
        ),
      };
    },
    [propertyId]
  );

  const handlePropertyContact = useCallback(
    async (form: ContactFormData) => {
      const response = await fetch("/api/contact/send", {
        method: "post",
        body: JSON.stringify({
          ...form,
          property_id: propertyId,
        }),
        headers: {
          "content-type": "application/json",
        },
      });

      return response.status >= 200 && response.status <= 299;
    },
    [propertyId]
  );

  return (
    <>
      <Head>
        <title>{title} - FeelingHome</title>
        <meta property="og:title" content={`${title} - Feeling Home`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        {photos.length > 0 && (
          <meta
            property="og:image"
            content={`${config.backUrl}/assets/${photos[0].resource}?fit=cover&width=256&height=256`}
          />
        )}
      </Head>
      <HomeSheetTemplate
        title={title}
        beds={beds}
        doors={doors}
        people={people}
        minPrice={minPrice}
        description={description}
        amenities={amenities}
        photos={photos}
        onBookingRequest={handleBookingRequest}
        onCalculatePrice={handleCalculatePrice}
        searchFormData={searchFormData}
        onGetCalendar={handleGetCalendar}
        onPropertyContact={handlePropertyContact}
      />
    </>
  );
}
